/**
 * Requirement Sync API Service
 *
 * REST client for /api/requirement-sync — keeps tickets and the CAB in step with each other
 * (Web projects): status of both directions, background sync jobs, review and apply.
 */

import api from '../utils/axios';

const BASE = '/requirement-sync';

export type SyncState = 'in_sync' | 'changes_pending' | 'not_generated' | 'no_baseline' | 'busy';
export type SyncJobStatus = 'queued' | 'running' | 'completed' | 'applying' | 'applied' | 'discarded' | 'failed' | 'cancelled';
export type SyncProposalAction = 'update' | 'create' | 'delete';
export type SyncDecision = 'accepted' | 'rejected';

export interface SyncChangeCounts {
  modified: number;
  created: number;
  deleted: number;
}

export const TICKETS_TO_CAB = 'tickets->cab';
export const CAB_TO_TICKETS = 'cab->tickets';

export interface SyncProposalField {
  field: string;
  old: unknown;
  new: unknown;
  /** CAB proposals: the field is removed from the item. */
  removed?: boolean;
}

export interface SyncProposal {
  proposalId: string;
  /** "ticket" (from CAB changes) or "cab" (from ticket changes). */
  kind: 'ticket' | 'cab';
  action: SyncProposalAction;
  itemId: string | null;
  title: string;
  status: string | null;
  /** CAB proposals: the CAB section, e.g. "User Stories". */
  sectionLabel: string | null;
  fields: SyncProposalField[];
  /** CAB proposals: the tickets whose change this carries. */
  basis: string[];
  /** CAB removals: open tickets, other than the basis, that still point to the item. */
  stillLinked: string[];
  author: string;
  /** Set when the change is shown but cannot be applied, e.g. "closed". */
  blockedReason: string | null;
}

export interface SyncLink {
  section: string;
  itemId: string | null;
  name: string | null;
  label: string;
}

/** What a tickets->CAB run concluded for one changed ticket. */
export interface SyncOutcome {
  ticketId: string;
  title: string;
  change: 'modified' | 'created' | 'deleted';
  /** carried | already_stated | not_requirement | question | no_change | previously_rejected | unhandled */
  outcome: string;
  note: string;
  links: SyncLink[];
}

export interface SyncJobResult {
  proposals: SyncProposal[];
  outcomes: SyncOutcome[];
  summary: string;
  changes: SyncChangeCounts;
}

export interface SyncJob {
  jobId: string;
  ruleId: string;
  status: SyncJobStatus;
  pollIntervalSec: number;
  error: string | null;
  /** Cancel was asked for; the run stops within a few seconds. */
  cancelRequested: boolean;
  result: SyncJobResult | null;
  /** The impact check that starts by itself once ticket edits changed the CAB: the tickets→CAB job it follows. */
  followupOf: string | null;
  /** The tickets whose edits made that CAB change. */
  followupTickets: string[];
  /** That impact check found no ticket to change, so it was recorded as synced without a review. */
  autoSettled: boolean;
  /** While the review waits for a decision: the tickets it is about, which can't be edited or deleted until then. */
  lockedTickets: string[];
}

export interface SyncRuleStatus {
  ruleId: string;
  title: string;
  source: string;
  target: string;
  state: SyncState;
  changes: SyncChangeCounts;
  since: string | null;
  reason: string | null;
  /** This rule waits for another one (ticket edits reach the CAB before the CAB updates tickets). */
  blockedBy: string | null;
  job: SyncJob | null;
}

export interface SyncApplyResult {
  applied: number;
  accepted: number;
  rejected: number;
  created: string[];
  skipped: Array<{ proposalId: string | null; itemId: string | null; reason: string }>;
  /** Tickets whose change is still pending for the next run. */
  stillPending: string[];
}

/** One CAB proposal of a tickets->CAB review, as placed in the highlighted CAB. */
export interface SyncCabReviewItem {
  proposalId: string;
  /** The item's path in the CAB, e.g. "userStories[3]" or "projectIdentity"; null when it could not be placed. */
  path: string | null;
  /** Highlights in the HTML that belong to this proposal; 0 = not shown in the document. */
  highlights: number;
  /** Why applying would skip it (e.g. the item was edited since the sync ran); null when it applies cleanly. */
  problem: string | null;
  /** Updates: the changed fields no highlight shows (e.g. a field the CAB view does not draw); empty otherwise. */
  hiddenFields: string[];
}

export interface SyncCabReview {
  available: boolean;
  /** Why the inline review is not available (available = false). */
  reason: string | null;
  /** The CAB with this review's changes highlighted; each highlight carries data-sync-proposal="<proposal id>". */
  html: string | null;
  items: SyncCabReviewItem[];
}

// ─── API shapes (snake_case from backend) ────────────────────────────────────

interface ApiProposal {
  proposal_id: string;
  kind?: 'ticket' | 'cab';
  action: SyncProposalAction;
  item_id: string | null;
  title: string;
  status: string | null;
  section_label?: string | null;
  fields: SyncProposalField[];
  basis?: string[];
  still_linked?: string[];
  author: string;
  blocked_reason: string | null;
}

interface ApiLink {
  section: string;
  item_id: string | null;
  name: string | null;
  label: string;
}

interface ApiOutcome {
  ticket_id: string;
  title: string;
  change: 'modified' | 'created' | 'deleted';
  outcome: string;
  note: string;
  links: ApiLink[];
}

interface ApiJob {
  job_id: string;
  rule_id: string;
  status: SyncJobStatus;
  poll_interval_sec: number;
  error: string | null;
  cancel_requested?: boolean;
  result: { proposals: ApiProposal[]; outcomes?: ApiOutcome[]; summary: string; changes: SyncChangeCounts } | null;
  followup_of?: string | null;
  followup_tickets?: string[];
  auto_settled?: boolean;
  locked_tickets?: string[];
}

interface ApiRuleStatus {
  rule_id: string;
  title: string;
  source: string;
  target: string;
  state: SyncState;
  changes: SyncChangeCounts;
  since: string | null;
  reason: string | null;
  blocked_by?: string | null;
  job: ApiJob | null;
}

interface ApiCabReview {
  available: boolean;
  reason?: string | null;
  html?: string | null;
  items?: Array<{
    proposal_id: string; path?: string | null; highlights?: number; problem?: string | null; hidden_fields?: string[];
  }>;
}

// ─── Mappers ──────────────────────────────────────────────────────────────────

function mapJob(j: ApiJob): SyncJob {
  return {
    jobId: j.job_id,
    ruleId: j.rule_id,
    status: j.status,
    pollIntervalSec: j.poll_interval_sec,
    error: j.error,
    cancelRequested: Boolean(j.cancel_requested),
    followupOf: j.followup_of ?? null,
    followupTickets: j.followup_tickets ?? [],
    autoSettled: Boolean(j.auto_settled),
    lockedTickets: j.locked_tickets ?? [],
    result: j.result && {
      summary: j.result.summary,
      changes: j.result.changes,
      proposals: j.result.proposals.map(p => ({
        proposalId: p.proposal_id,
        kind: p.kind ?? 'ticket',
        action: p.action,
        itemId: p.item_id,
        title: p.title,
        status: p.status,
        sectionLabel: p.section_label ?? null,
        fields: p.fields,
        basis: p.basis ?? [],
        stillLinked: p.still_linked ?? [],
        author: p.author,
        blockedReason: p.blocked_reason,
      })),
      outcomes: (j.result.outcomes ?? []).map(o => ({
        ticketId: o.ticket_id,
        title: o.title,
        change: o.change,
        outcome: o.outcome,
        note: o.note,
        links: (o.links ?? []).map(l => ({ section: l.section, itemId: l.item_id, name: l.name, label: l.label })),
      })),
    },
  };
}

function mapRuleStatus(r: ApiRuleStatus): SyncRuleStatus {
  return {
    ruleId: r.rule_id,
    title: r.title,
    source: r.source,
    target: r.target,
    state: r.state,
    changes: r.changes,
    since: r.since,
    reason: r.reason,
    blockedBy: r.blocked_by ?? null,
    job: r.job ? mapJob(r.job) : null,
  };
}

// ─── API functions ────────────────────────────────────────────────────────────

/** Every rule the project runs, in the order they run; empty when the project does not sync here. */
export async function getSyncStatus(projectId: string): Promise<SyncRuleStatus[]> {
  const res = await api.get<{ rules: ApiRuleStatus[] }>(`${BASE}/${projectId}/status`);
  return res.data.rules.map(mapRuleStatus);
}

export async function startSyncJob(projectId: string, ruleId: string): Promise<SyncJob> {
  const res = await api.post<ApiJob>(`${BASE}/${projectId}/jobs`, { rule_id: ruleId });
  return mapJob(res.data);
}

export async function getSyncJob(projectId: string, jobId: string): Promise<SyncJob> {
  const res = await api.get<ApiJob>(`${BASE}/${projectId}/jobs/${jobId}`);
  return mapJob(res.data);
}

/** The CAB as an open tickets->CAB review would change it, highlighted; or why it cannot be shown (available = false). */
export async function getSyncCabReview(projectId: string, jobId: string): Promise<SyncCabReview> {
  const res = await api.get<ApiCabReview>(`${BASE}/${projectId}/jobs/${jobId}/cab-review`);
  return {
    available: res.data.available,
    reason: res.data.reason ?? null,
    html: res.data.html ?? null,
    items: (res.data.items ?? []).map(i => ({
      proposalId: i.proposal_id,
      path: i.path ?? null,
      highlights: i.highlights ?? 0,
      problem: i.problem ?? null,
      hiddenFields: i.hidden_fields ?? [],
    })),
  };
}

export async function cancelSyncJob(projectId: string, jobId: string): Promise<void> {
  await api.post(`${BASE}/${projectId}/jobs/${jobId}/cancel`);
}

export async function applySyncJob(
  projectId: string,
  jobId: string,
  decisions: Record<string, SyncDecision>,
): Promise<SyncApplyResult> {
  const res = await api.post<{
    applied: number; accepted: number; rejected: number; created: string[];
    skipped: Array<{ proposal_id: string | null; item_id: string | null; reason: string }>;
    still_pending?: string[];
  }>(`${BASE}/${projectId}/jobs/${jobId}/apply`, { decisions });
  return {
    applied: res.data.applied,
    accepted: res.data.accepted,
    rejected: res.data.rejected,
    created: res.data.created,
    skipped: res.data.skipped.map(s => ({ proposalId: s.proposal_id, itemId: s.item_id, reason: s.reason })),
    stillPending: res.data.still_pending ?? [],
  };
}

/** Whether a job's review still waits for a decision: false once it was handled or is gone, null when that
 *  could not be read. */
export async function isSyncReviewOpen(projectId: string, jobId: string): Promise<boolean | null> {
  try {
    return (await getSyncJob(projectId, jobId)).status === 'completed';
  } catch (err) {
    return (err as { response?: { status?: number } })?.response?.status === 410 ? false : null;
  }
}

/** Discard All: reject every proposal. The CAB or tickets stay as they are, the edits count as handled, and the
 *  same changes are not proposed again. */
export function rejectAllDecisions(job: SyncJob): Record<string, SyncDecision> {
  return Object.fromEntries((job.result?.proposals ?? []).map(p => [p.proposalId, 'rejected' as const]));
}

/** The backend's error detail, or a fallback. */
export function syncErrorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
}
